import { useState } from "react";

import styles from "./TaskView.module.css";
import { useSession } from "../../auth/session";
import { Button } from "../../components/Button";
import { isSandboxUnavailable } from "../../data/api/client";
import { startAllProcesses, useTaskProcesses } from "../../data/api/services";
import { useRealtimeScope } from "../../data/realtime/useRealtimeScope";
import { Icon } from "../../icons";

export type PreviewControlsProps = {
  /**
   * Re-read the task, having learned from a refusal that its sandbox is gone.
   * The event that says so is on its way (`sandboxes.md` §Teardown ends what the
   * container was holding); this is for the page that misses it.
   */
  readonly onSandboxLost: () => void;
  /**
   * This task's sandbox is an on-demand instance, which previews are not
   * provisioned for (`on-demand-services.md` §Non-goal).
   */
  readonly previewsDisabled: boolean;
  readonly provisioning: boolean;
  /** This task's sandbox has been torn down — see `TaskDetail.sandboxReleased`. */
  readonly sandboxReleased: boolean;
  readonly taskId: string;
};

// The task header's "Run app" / "Open preview" (live-branch-previews.md §5
// The affordance): one control whose meaning follows the state of the task's
// declared processes, rather than a second surface next to the Services tab —
// that stays the canonical place to see and control each process on its own.
export const PreviewControls = ({
  onSandboxLost,
  previewsDisabled,
  provisioning,
  sandboxReleased,
  taskId,
}: PreviewControlsProps) => {
  const { user } = useSession();
  const { processes, refetch } = useTaskProcesses(taskId);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Without this, the initial post-click refetch is the only one this
  // component ever does: the daemon confirms "starting" → "up" and opens the
  // tunnel well after that request resolves, so the button would stay stuck
  // on its optimistic state until something else remounted it (ServicesTab
  // has the same subscription, for the same reason).
  //
  // `sandbox.declarations.updated` is the one that fires before any of this:
  // a reader who opened the task while it was provisioning fetched an empty
  // list, because the sandbox that owns the rows did not exist yet.
  useRealtimeScope(`task:${taskId}`, (event) => {
    if (
      event.kind === "process.status.changed" ||
      event.kind === "sandbox.declarations.updated" ||
      event.kind === "tunnel.opened" ||
      event.kind === "tunnel.closed" ||
      event.kind === "resync"
    ) {
      refetch();
    }
  });

  // A deployment-wide fact, not a per-task one: `TIN_COOKIE_DOMAIN`/
  // `TIN_PREVIEW_HOST` disagreeing means every preview link 403s no matter
  // what this task has running, so this is said once here rather than
  // offering a link that cannot work (deploy/README.md §Public previews).
  if (user && !user.previews_configured) {
    return (
      <span className={styles.prConflict}>
        Previews aren't configured on this deployment.
      </span>
    );
  }

  // A task placed on a runner that serves sandboxes as on-demand instances:
  // previews are keyed off the sandbox rows a real task owns, and nothing
  // reserves one for a container beside another sandbox. Said plainly, because
  // the alternative is a "Run app" that works and an "Open preview" that never
  // appears (`on-demand-services.md` §Non-goal).
  if (previewsDisabled) {
    return <span className={styles.prConflict}>Previews disabled</span>;
  }

  // `declared_processes` rows exist as soon as the sandbox reads
  // `.tinsmith/config.json`, well before its daemon dials in — a "Run app"
  // click in that window has no daemon to reach and would strand the process
  // in `starting` forever (server rejects it too, see `processes.ts`
  // `start-all`, but this is what stops the click from having anything to
  // reject).
  if (provisioning) {
    return <span className={styles.prConflict}>Sandbox is starting…</span>;
  }

  // The other end of the same life: the sandbox has been torn down and nothing
  // re-provisions one (`sandboxes.md` §Reclamation policy), so there is no app
  // left to run and no preview left to open. Said rather than left blank,
  // because this is the one notice that also accounts for the Services tab's
  // missing controls and the Terminals tab's missing "+ new shell".
  if (sandboxReleased) {
    return <span className={styles.prConflict}>Sandbox is gone</span>;
  }

  // When more than one port is public, the one marked `primary` in
  // `.tinsmith/config.json` wins; absent that, the first declared with a live
  // tunnel (tinsmith-config-file.md ports[].primary).
  const livePorts = processes
    .flatMap((process) => process.ports)
    .filter((port) => port.previewUrl);
  const live = (livePorts.find((port) => port.isPrimary) ?? livePorts[0])
    ?.previewUrl;
  if (live) {
    return (
      <a className={styles.prPill} href={live} rel="noreferrer" target="_blank">
        Open preview <Icon.External size={10} />
      </a>
    );
  }

  // Between the click and the tunnel opening, a public process spends a
  // while `starting` (or already `up` with the tunnel not open yet) — with
  // nothing shown for that stretch, the button just vanishes into blank
  // space. Show progress instead of nothing.
  const publicProcesses = processes.filter((process) =>
    process.ports.some((port) => port.isPublic),
  );
  const pending =
    starting || publicProcesses.some((process) => process.status !== "down");
  if (pending) {
    return (
      <Button className={styles.rightAction} disabled size="sm">
        Starting…
      </Button>
    );
  }

  // Nothing is reachable yet. Offered only while there is something a start
  // would actually do — a process already up or starting is not helped by
  // asking again, and once every declared process is up with nothing public
  // the fix is toggling a port in the Services tab, not this button.
  const startable = processes.some(
    (process) => process.status !== "up" && process.status !== "starting",
  );
  if (!startable) {
    return null;
  }

  const onStart = (): void => {
    setStarting(true);
    setError(null);
    // Cleared only on failure, so the retry-able "Run app" comes back. On
    // success it stays true until `pending` picks up the real process status
    // from a refetch — `refetch()` itself doesn't return a promise (it just
    // schedules one), so clearing this the moment it's called would reopen
    // the exact gap above for however long that fetch takes to land.
    void startAllProcesses(taskId)
      .then(() => refetch())
      .catch((err: unknown) => {
        setStarting(false);
        setError(err instanceof Error ? err.message : "Request failed.");
        // The refusal is the whole answer: this button goes with the rest of
        // the sandbox's controls once the reread lands, message and all.
        if (isSandboxUnavailable(err)) {
          onSandboxLost();
        }
      });
  };

  return (
    <span className={styles.prControls}>
      {error ? (
        <span className={styles.prError} role="alert" title={error}>
          {error}
        </span>
      ) : null}
      <Button className={styles.rightAction} onClick={onStart} size="sm">
        <Icon.Play size={12} /> Run app
      </Button>
    </span>
  );
};
